import React, { memo, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Typography,
  Box,
} from '@mui/material';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import type { DoctorShift } from '../../../../../types/doctorShift';

/**
 * SlotSummaryTable Props
 */
interface SlotSummaryTableProps {
  /** シフト一覧 */
  shifts: DoctorShift[];
  /** 表示対象の日付配列 */
  dates: Date[];
  /** 表示モード（週/月） */
  mode: 'week' | 'month';
}

/**
 * 日付ごとの集計データ
 */
interface DailySummary {
  date: string;
  dateLabel: string;
  totalShifts: number;
  normalSlots: number;
  urgentSlots: number;
}

/**
 * 日付ごとにシフトを集計
 */
const calculateDailySummaries = (
  shifts: DoctorShift[],
  dates: Date[]
): DailySummary[] => {
  return dates.map((date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const dayShifts = shifts.filter((shift) => shift.shift_date === dateStr);

    return {
      date: dateStr,
      dateLabel: format(date, 'M/d(E)', { locale: ja }),
      totalShifts: dayShifts.length,
      normalSlots: dayShifts.filter((s) => s.slot_type !== 'urgent').length,
      urgentSlots: dayShifts.filter((s) => s.slot_type === 'urgent').length,
    };
  });
};

/**
 * 枠集計テーブルコンポーネント
 *
 * @description
 * 週/月単位でのシフト枠集計を表形式で表示する。
 *
 * @param {SlotSummaryTableProps} props - コンポーネントProps
 * @returns {React.ReactElement} 枠集計テーブル
 *
 * @example
 * <SlotSummaryTable
 *   shifts={shifts}
 *   dates={weekDates}
 *   mode="week"
 * />
 */
const SlotSummaryTable: React.FC<SlotSummaryTableProps> = memo(
  ({ shifts, dates, mode }) => {
    const summaries = useMemo(
      () => calculateDailySummaries(shifts, dates),
      [shifts, dates]
    );

    // 合計計算
    const totals = useMemo(() => {
      return summaries.reduce(
        (acc, summary) => ({
          totalShifts: acc.totalShifts + summary.totalShifts,
          normalSlots: acc.normalSlots + summary.normalSlots,
          urgentSlots: acc.urgentSlots + summary.urgentSlots,
        }),
        { totalShifts: 0, normalSlots: 0, urgentSlots: 0 }
      );
    }, [summaries]);

    return (
      <TableContainer component={Paper}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>日付</TableCell>
              <TableCell align="center">シフト数</TableCell>
              <TableCell align="center">通常枠</TableCell>
              <TableCell align="center">急募枠</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {summaries.map((summary) => (
              <TableRow
                key={summary.date}
                hover
                sx={{
                  backgroundColor:
                    summary.urgentSlots > 0 ? 'error.50' : undefined,
                }}
              >
                <TableCell>
                  <Typography variant="body2">{summary.dateLabel}</Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2">{summary.totalShifts}</Typography>
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={summary.normalSlots}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={summary.urgentSlots}
                    size="small"
                    color={summary.urgentSlots > 0 ? 'error' : 'default'}
                    variant={summary.urgentSlots > 0 ? 'filled' : 'outlined'}
                  />
                </TableCell>
              </TableRow>
            ))}

            {/* 合計行 */}
            <TableRow sx={{ backgroundColor: 'grey.100' }}>
              <TableCell>
                <Typography variant="body2" fontWeight="bold">
                  合計 ({mode === 'week' ? '週' : '月'})
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="body2" fontWeight="bold">
                  {totals.totalShifts}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Chip
                  label={totals.normalSlots}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </TableCell>
              <TableCell align="center">
                <Chip
                  label={totals.urgentSlots}
                  size="small"
                  color={totals.urgentSlots > 0 ? 'error' : 'default'}
                  variant={totals.urgentSlots > 0 ? 'filled' : 'outlined'}
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>

        {/* データがない場合 */}
        {summaries.length === 0 && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              表示するデータがありません
            </Typography>
          </Box>
        )}
      </TableContainer>
    );
  }
);

SlotSummaryTable.displayName = 'SlotSummaryTable';

export { SlotSummaryTable };
